class SlideshowComponent extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.swiperContainer = null;
    this.prevButton = null;
    this.nextButton = null;
    this.progressBar = null;
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
    }
  }

  // Helper method to get responsive values based on breakpoints
  getResponsiveValue(baseAttribute) {
    const breakpoints = {
      mobile: { max: 768, suffix: "-mobile" },
      tablet: { min: 769, max: 1023, suffix: "-tablet" },
      laptop: { min: 1024, max: 1439, suffix: "-laptop" },
      desktop: { min: 1440, suffix: "-desktop" },
    };

    // Check for device-specific values first
    for (const [device, config] of Object.entries(breakpoints)) {
      const value =
        this.dataset[baseAttribute + config.suffix.replace("-", "")];
      if (value !== undefined) {
        return { [device]: value, ...breakpoints[device] };
      }
    }

    // Fallback to base attribute or default
    const fallbackValue = this.dataset[baseAttribute];
    return fallbackValue;
  }

  // Create Swiper breakpoints configuration
  createBreakpointsConfig() {
    const breakpoints = {};

    // Get responsive values for numberOfItems
    const smallMobileItems = this.dataset.numberOfItemsSmallMobile;
    const mobileItems = this.dataset.numberOfItemsMobile;
    const tabletItems = this.dataset.numberOfItemsTablet;
    const laptopItems = this.dataset.numberOfItemsLaptop;
    const desktopItems = this.dataset.numberOfItemsDesktop;

    // Get responsive values for gap
    const smallMobileGap = this.dataset.gapSmallMobile;
    const mobileGap = this.dataset.gapMobile;
    const tabletGap = this.dataset.gapTablet;
    const laptopGap = this.dataset.gapLaptop;
    const desktopGap = this.dataset.gapDesktop;

    // Default values
    const defaultItems = parseInt(this.dataset.numberOfItems) || 1;
    const defaultGap = parseInt(this.dataset.gap) || 16;

    // Rows only ever apply on mobile (a real N-column x M-row product
    // matrix, e.g. 2x2 or 3x2 — never a single item spanning rows). Applied
    // to both mobile tiers below since no section splits rows by width.
    const mobileRows = parseInt(this.dataset.rowsMobile) || 1;

    // Small mobile (up to 480px) — falls back to the regular mobile value
    // when not set, so sections without this attribute behave exactly as
    // before the small-mobile tier existed.
    if (smallMobileItems || smallMobileGap || mobileItems || mobileGap || mobileRows > 1) {
      breakpoints[0] = {
        slidesPerView: parseInt(smallMobileItems) || parseInt(mobileItems) || defaultItems,
        spaceBetween: parseInt(smallMobileGap) || parseInt(mobileGap) || defaultGap,
      };

      if (mobileRows > 1) {
        breakpoints[0].grid = { rows: mobileRows, fill: "row" };
      }
    }

    // Mobile (481px to 768px)
    if (mobileItems || mobileGap || mobileRows > 1) {
      breakpoints[481] = {
        slidesPerView: parseInt(mobileItems) || defaultItems,
        spaceBetween: parseInt(mobileGap) || defaultGap,
      };

      if (mobileRows > 1) {
        breakpoints[481].grid = { rows: mobileRows, fill: "row" };
      }
    }

    // Tablet (769px to 1023px)
    if (tabletItems || tabletGap) {
      breakpoints[769] = {
        slidesPerView: parseInt(tabletItems) || defaultItems,
        spaceBetween: parseInt(tabletGap) || defaultGap,
      };
    }

    // Laptop (1024px to 1439px)
    if (laptopItems || laptopGap) {
      breakpoints[1024] = {
        slidesPerView: parseInt(laptopItems) || defaultItems,
        spaceBetween: parseInt(laptopGap) || defaultGap,
      };
    }

    // Desktop (1440px and above)
    if (desktopItems || desktopGap) {
      breakpoints[1440] = {
        slidesPerView: parseInt(desktopItems) || defaultItems,
        spaceBetween: parseInt(desktopGap) || defaultGap,
      };
    }

    return breakpoints;
  }

  init() {
    this.swiperContainer = this.querySelector(".swiper-container");
    this.prevButtons = Array.from(
      this.querySelectorAll(".slideshow-nav-button-prev")
    );
    this.nextButtons = Array.from(
      this.querySelectorAll(".slideshow-nav-button-next")
    );
    // Primary pair kept for backwards compatibility (Swiper's built-in
    // navigation module only accepts a single element per side).
    this.prevButton = this.prevButtons[0] || null;
    this.nextButton = this.nextButtons[0] || null;
    this.progressBar = this.querySelector(".swiper-pagination");

    this.querySelectorAll("video").forEach((video) => {
      if (video.readyState >= 2) {
        const skeleton = video.previousElementSibling;
        if (skeleton && skeleton.classList.contains("video-skeleton")) {
          skeleton.remove();
        }
      }
    });

    if (!this.swiperContainer) {
      console.error("Swiper container not found");
      return;
    }

    // Count the number of slides
    const slideCount = this.querySelectorAll(".swiper-slide").length;

    const autoplay = this.dataset.autoplay === "true";
    const autoplayDelay = parseInt(this.dataset.autoplayDelay) || 5000;
    const pauseOnHover = this.dataset.pauseOnHover === "true";
    const enableCarousel = this.dataset.enableCarousel === "true";
    const showPartialSlides = this.dataset.showPartialSlides === "true";
    const showProgressBar = this.dataset.showProgressBar === "true";
    const showDots = this.dataset.showDots === "true";
    const numberOfItems = parseInt(this.dataset.numberOfItems) || 0;
    const gap = this.dataset.gap ? parseInt(this.dataset.gap) : 16;

    // New data attribute for navigation loop control (default: true)
    const navLoop = this.dataset.navLoop !== "false";

    const swiperOptions = {
      loop: slideCount > 1 && navLoop, // Use navLoop for loop control
      spaceBetween: gap,
      allowTouchMove: slideCount > 1 ? enableCarousel : false, // Disable touch if only 1 slide
      // A base grid config (rows: 1) is a documented no-op for Swiper's
      // Grid module, but its presence here is required for a later
      // breakpoint override (e.g. data-rows-mobile) to actually restructure
      // the layout into real columns x rows instead of just affecting height.
      grid: {
        rows: 1,
        fill: "row",
      },
      navigation: {
        nextEl: this.nextButton,
        prevEl: this.prevButton,
      },
      on: {
        init: () => this.updateNavigationState(),
        slideChange: () => this.updateNavigationState(),
      },
    };

    // Add responsive breakpoints
    const responsiveBreakpoints = this.createBreakpointsConfig();
    if (Object.keys(responsiveBreakpoints).length > 0) {
      swiperOptions.breakpoints = responsiveBreakpoints;
    }

    if (enableCarousel && slideCount > 1) {
      Object.assign(swiperOptions, {
        grabCursor: true,
        freeMode: {
          enabled: true,
          momentum: true,
          sticky: false,
        },
        loop: navLoop, // Use navLoop for carousel loop as well
        watchSlidesProgress: true,
        watchSlidesVisibility: true,
      });

      // Breakpoints (mobile/tablet/laptop/desktop) already give Swiper an
      // explicit slidesPerView per device, so it sizes and positions slides
      // itself (grid-aware, when rows are involved). Only fall back to
      // "auto" + enforceSlideWidths — which re-measures each slide from its
      // CSS width after Swiper's own layout pass — when there's no numeric
      // slidesPerView anywhere, otherwise the extra pass fights Swiper's
      // layout and causes a visible flicker/reflow right after init.
      const hasResponsiveSlidesPerView = Object.values(
        responsiveBreakpoints
      ).some((bp) => bp.slidesPerView !== undefined);

      if (numberOfItems > 0) {
        swiperOptions.slidesPerView = numberOfItems;
      } else if (hasResponsiveSlidesPerView) {
        swiperOptions.slidesPerView = responsiveBreakpoints[0]?.slidesPerView || 1;
      } else {
        swiperOptions.slidesPerView = "auto";
        swiperOptions.on = {
          ...swiperOptions.on,
          init: () => {
            this.updateNavigationState();
            this.enforceSlideWidths();
          },
        };
      }
    } else if (numberOfItems > 0) {
      swiperOptions.slidesPerView = numberOfItems;
    } else if (showPartialSlides) {
      // For partial slides, allow touch move even with 1 slide for visual effect
      swiperOptions.allowTouchMove = slideCount > 1;
      swiperOptions.slidesPerView = 1;
      swiperOptions.centeredSlides = false;
      swiperOptions.centerInsufficientSlides = true;

      // Only apply partial slide width if there's more than 1 slide
      if (slideCount > 1) {
        // Merge with existing breakpoints if any, otherwise create new ones
        if (!swiperOptions.breakpoints) {
          swiperOptions.breakpoints = {};
        }

        // Add the partial slides breakpoint, but don't override existing responsive settings
        if (!swiperOptions.breakpoints[769]) {
          swiperOptions.breakpoints[769] = {
            slidesPerView: 1.2,
            centeredSlides: true,
            spaceBetween: gap,
          };
        } else {
          // If there's already a tablet breakpoint, add the partial slides settings to it
          swiperOptions.breakpoints[769] = {
            ...swiperOptions.breakpoints[769],
            slidesPerView: swiperOptions.breakpoints[769].slidesPerView || 1.2,
            centeredSlides: true,
          };
        }
      }
      // If only 1 slide, it will use slidesPerView: 1 (full width) from above
    } else {
      swiperOptions.slidesPerView = 1;
    }

    if (showDots && this.progressBar && slideCount > 1) {
      swiperOptions.pagination = {
        el: this.progressBar,
        type: "bullets",
        clickable: true,
      };
    } else if (showProgressBar && this.progressBar && slideCount > 1) {
      swiperOptions.pagination = {
        el: this.progressBar,
        type: "progressbar",
        progressbarOpposite: false,
      };
    }

    // Only enable autoplay if there's more than 1 slide
    if (autoplay && slideCount > 1) {
      swiperOptions.autoplay = {
        delay: autoplayDelay,
        disableOnInteraction: false,
        pauseOnMouseEnter: pauseOnHover,
        waitForTransition: true,
        reverseDirection: false,
        stopOnLastSlide: enableCarousel ? true : false,
      };

      if (enableCarousel) {
        swiperOptions.autoplay.disableOnInteraction = true;
      }
    }

    if (window.Swiper) {
      this.swiper = new Swiper(this.swiperContainer, swiperOptions);
      this.setupAutoplayInteractions();
      this.bindExtraNavButtons();
    } else {
      this.waitForSwiper().then(() => {
        this.swiper = new Swiper(this.swiperContainer, swiperOptions);
        this.setupAutoplayInteractions();
        this.bindExtraNavButtons();
      });
    }
  }

  // Swiper's built-in navigation only wires up a single prev/next element
  // per side (this.prevButton/this.nextButton). Any additional matching
  // buttons in the DOM (e.g. a duplicate set shown only on mobile) are
  // wired up here so every copy controls the same swiper instance.
  bindExtraNavButtons() {
    if (!this.swiper) return;

    this.prevButtons.slice(1).forEach((button) => {
      button.addEventListener("click", () => {
        if (!button.disabled) this.swiper.slidePrev();
      });
    });

    this.nextButtons.slice(1).forEach((button) => {
      button.addEventListener("click", () => {
        if (!button.disabled) this.swiper.slideNext();
      });
    });
  }

  waitForSwiper() {
    return new Promise((resolve) => {
      const checkSwiper = () => {
        if (window.Swiper) {
          resolve();
        } else {
          setTimeout(checkSwiper, 100);
        }
      };
      checkSwiper();
    });
  }

  updateNavigationState() {
    if (!this.swiper) return;

    const slideCount = this.querySelectorAll(".swiper-slide").length;
    const navLoop = this.dataset.navLoop !== "false"; // Get the navLoop setting

    if (this.prevButton) {
      let prevDisabled;
      if (navLoop) {
        // Original behavior: disable if 1 slide or at beginning without loop
        prevDisabled =
          slideCount <= 1 ||
          (this.swiper.isBeginning && !this.swiper.params.loop);
      } else {
        // New behavior: disable if 1 slide or at the very beginning (no loop)
        prevDisabled = slideCount <= 1 || this.swiper.isBeginning;
      }
      (this.prevButtons || [this.prevButton]).forEach((button) => {
        button.disabled = prevDisabled;
        button.setAttribute("aria-disabled", prevDisabled);
      });
    }

    if (this.nextButton) {
      let nextDisabled;
      if (navLoop) {
        // Original behavior: disable if 1 slide or at end without loop
        nextDisabled =
          slideCount <= 1 || (this.swiper.isEnd && !this.swiper.params.loop);
      } else {
        // New behavior: disable if 1 slide or at the very end (no loop)
        nextDisabled = slideCount <= 1 || this.swiper.isEnd;
      }
      (this.nextButtons || [this.nextButton]).forEach((button) => {
        button.disabled = nextDisabled;
        button.setAttribute("aria-disabled", nextDisabled);
      });
    }
  }

  setupAutoplayInteractions() {
    if (!this.swiper || !this.swiper.autoplay) return;

    const slideCount = this.querySelectorAll(".swiper-slide").length;
    const autoplay = this.dataset.autoplay === "true";
    const pauseOnHover = this.dataset.pauseOnHover === "true";
    const enableCarousel = this.dataset.enableCarousel === "true";

    // Don't setup autoplay interactions if only 1 slide
    if (!autoplay || slideCount <= 1) return;

    if (pauseOnHover) {
      this.addEventListener("mouseenter", () => {
        if (this.swiper && this.swiper.autoplay) {
          this.swiper.autoplay.pause();
        }
      });

      this.addEventListener("mouseleave", () => {
        if (this.swiper && this.swiper.autoplay) {
          this.swiper.autoplay.resume();
        }
      });
    }

    if (enableCarousel) {
      let interactionTimeout;

      const resumeAutoplay = () => {
        clearTimeout(interactionTimeout);
        interactionTimeout = setTimeout(() => {
          if (
            this.swiper &&
            this.swiper.autoplay &&
            !this.swiper.autoplay.running
          ) {
            this.swiper.autoplay.start();
          }
        }, 2000);
      };

      this.swiperContainer.addEventListener("touchend", resumeAutoplay);
      this.swiperContainer.addEventListener("mouseup", resumeAutoplay);
    }
  }

  slideNext() {
    const slideCount = this.querySelectorAll(".swiper-slide").length;
    if (this.swiper && slideCount > 1) {
      this.swiper.slideNext();
    }
  }

  slidePrev() {
    const slideCount = this.querySelectorAll(".swiper-slide").length;
    if (this.swiper && slideCount > 1) {
      this.swiper.slidePrev();
    }
  }

  slideTo(index) {
    const slideCount = this.querySelectorAll(".swiper-slide").length;
    if (this.swiper && slideCount > 1) {
      this.swiper.slideTo(index);
    }
  }

  startAutoplay() {
    const slideCount = this.querySelectorAll(".swiper-slide").length;
    if (this.swiper && this.swiper.autoplay && slideCount > 1) {
      this.swiper.autoplay.start();
    }
  }

  stopAutoplay() {
    if (this.swiper && this.swiper.autoplay) {
      this.swiper.autoplay.stop();
    }
  }

  setProgress(progress) {
    if (this.swiper && this.swiper.setProgress) {
      this.swiper.setProgress(progress);
    }
  }

  getProgress() {
    if (this.swiper && this.swiper.progress !== undefined) {
      return this.swiper.progress;
    }
    return 0;
  }

  enableTouch() {
    const slideCount = this.querySelectorAll(".swiper-slide").length;
    if (
      this.swiper &&
      this.swiper.allowTouchMove !== undefined &&
      slideCount > 1
    ) {
      this.swiper.allowTouchMove = true;
    }
  }

  disableTouch() {
    if (this.swiper && this.swiper.allowTouchMove !== undefined) {
      this.swiper.allowTouchMove = false;
    }
  }

  enforceSlideWidths() {
    const enableCarousel = this.dataset.enableCarousel === "true";
    if (!enableCarousel || !this.swiper) return;

    const slides = this.querySelectorAll(".swiper-slide");
    slides.forEach((slide) => {
      slide.style.width = "auto";
      slide.style.flexShrink = "0";

      const computedStyle = window.getComputedStyle(slide);
      if (computedStyle.width && computedStyle.width !== "auto") {
        slide.style.width = computedStyle.width;
      }
    });
  }
}

customElements.define("slideshow-component", SlideshowComponent);
